import React, { useEffect, useState, useRef } from "react";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton
} from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SessionStorageService from "../../../services/StorageService";
import MemurIzinTalepAPI, {
    MemIzinTalepListRequest,
    MemIzinTalepDelRequest
} from '../../../apis/MemurIzinTalepAPI';
import IsciIzinTalepAPI, {
    IsmIzinTalepListRequest,
    IsmIzinTalepDelRequest
} from '../../../apis/IsciIzinTalepAPI';
import TableComponent from "../../../components/OB/TableComponent";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const IzinTalepIslem = () => {
    const navigate = useNavigate();
    const [isLoad, setIsLoad] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const user = SessionStorageService.getUserInfo();

    // Sayfa yüklenme kontrolü - çift çağrıyı önlemek için
    const isPageLoadedRef = useRef(false);
    // API isteği devam ediyor mu kontrolü
    const isLoadingRef = useRef(false);

    const [perSicilBilgiKontrol, setPerSicilBilgiKontrol] = useState<any>(null);
    const [izinTalepList, setIzinTalepList] = useState<any[]>([]);

    // Silme onay dialog state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedDeleteItem, setSelectedDeleteItem] = useState<any>(null);

    // tarih formatlama yardımcı fonksiyonu
    const formatDate = (dateString: any): string => {
        try {
            if (!dateString) return "";
            const str = dateString.toString();

            // ISO format
            if (str.includes('T') || str.includes('-')) {
                // Sadece tarih kısmını al (YYYY-MM-DD) - timezone sorununu önlemek için
                const dateOnly = str.split('T')[0];
                const parts = dateOnly.split('-');
                if (parts.length === 3) {
                    const [year, month, day] = parts;
                    return `${day}.${month}.${year}`;
                }
            }

            // .NET JSON format
            const dotNetMatch = str.match(/\/Date\((\d+)([+-]\d{4})?\)\//);
            if (dotNetMatch) {
                const timestamp = parseInt(dotNetMatch[1], 10);
                // UTC timestamp'i local date olarak parse et
                const date = new Date(timestamp);
                // UTC değerlerini kullan (timezone shift olmadan)
                return `${date.getUTCDate().toString().padStart(2, '0')}.${(date.getUTCMonth() + 1).toString().padStart(2, '0')}.${date.getUTCFullYear()}`;
            }

            // Fallback: Date parse - UTC kullan
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return "";
            return `${date.getUTCDate().toString().padStart(2, '0')}.${(date.getUTCMonth() + 1).toString().padStart(2, '0')}.${date.getUTCFullYear()}`;
        } catch {
            return "";
        }
    };

    // Durum kodunu metne çevirme yardımcı fonksiyonu
    const getDurumText = (durum: number): string => {
        switch (durum) {
            case 0: return "İşlemde";
            case 1: return "Onaya Gönderildi";
            case 2: return "Onaylandı";
            case 3: return "Red";
            default: return "";
        }
    };

    // Tablo kolonları 
    const columns = [
        { header: "İzin & Rapor Türü", field: "izinTuru", className: "text-center" },
        { header: "Başlama Tarihi", field: "baslamaTarihi", className: "text-center" },
        { header: "Bitiş Tarihi", field: "bitisTarihi", className: "text-center" },
        { header: "Süresi", field: "suresi", className: "text-center" },
        { header: "Açıklama", field: "aciklama", className: "text-left" },
        { header: "Durum", field: "durumText", className: "text-center" },
        { header: "Durum Açıklama", field: "durumAciklama", className: "text-left" },
        {
            header: "Güncelle",
            field: "guncelle",
            className: "text-center",
            render: (row: any) => {
                // 1: Onaya Gönderildi, 2: Onaylandı, 3: Red - bu durumlarda güncellenemez
                const isDisabled = row.ctDurum === 1 || row.ctDurum === 2 || row.ctDurum === 3;
                return (
                    <IconButton
                        size="small"
                        color="primary"
                        onClick={() => !isDisabled && handleEdit(row)}
                        sx={{ opacity: isDisabled ? 0.5 : 1, cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                );
            }
        },
        {
            header: "Sil",
            field: "sil",
            className: "text-center",
            render: (row: any) => {
                // 1: Onaya Gönderildi, 2: Onaylandı, 3: Red - bu durumlarda silinemez
                const isDisabled = row.ctDurum === 1 || row.ctDurum === 2 || row.ctDurum === 3;
                return (
                    <IconButton
                        size="small"
                        color="error"
                        onClick={() => !isDisabled && handleDeleteClick(row)}
                        sx={{ opacity: isDisabled ? 0.5 : 1, cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                );
            }
        }
    ];

    // Memur mu İşçi mi kontrolü
    const isMemur = (memurIsci: string) => memurIsci === 'M';
    const isIsci = (memurIsci: string) => ['I', 'T', 'K'].includes(memurIsci);

    // İzin listesini yükle
    const loadIzinTalepList = async (memurIsci: string, sicilId: number): Promise<void> => {
        // Zaten bir istek devam ediyorsa yenisini engelle
        if (isLoadingRef.current) {
            console.log('İstek zaten devam ediyor, yeni istek engellendi');
            return;
        }

        try {
            isLoadingRef.current = true;
            let response;
            if (isMemur(memurIsci)) {
                // Memur için MEM_IZIN_TALEP_LIST
                const request = new MemIzinTalepListRequest(memurIsci, sicilId);
                response = await MemurIzinTalepAPI.memIzinTalepList(request);
            } else if (isIsci(memurIsci)) {
                // İşçi/Taşeron/Sözleşmeli için ISM_IZIN_TALEP_LIST
                const request = new IsmIzinTalepListRequest(memurIsci, sicilId);
                response = await IsciIzinTalepAPI.ismIzinTalepList(request);
            }

            if (response?.data) {
                const formattedData = response.data.map((item: any) => ({
                    ...item,
                    izinTuru: item.ctTur || "",
                    baslamaTarihi: formatDate(item.dtBaslangicTarihi),
                    bitisTarihi: formatDate(item.dtBitisTarihi),
                    suresi: item.qtSuresi || 0,
                    aciklama: item.chAciklama || "",
                    durumText: getDurumText(item.ctDurum),
                    durumAciklama: item.chDurumAciklama || ""
                }));
                setIzinTalepList(formattedData);
            }
        } catch (err) {
            console.error('İzin talep listesi yüklenirken hata:', err);
            toast.error("İzin listesi yüklenirken hata oluştu. Sayfayı yenileyin.");
        } finally {
            isLoadingRef.current = false;
        }
    };

    // Sayfa yüklendiğinde
    useEffect(() => {
        // StrictMode'da çift çağrıyı önle
        if (isPageLoadedRef.current) return;
        isPageLoadedRef.current = true;

        const sayfaYukle = async () => {
            try {
                setIsLoad(true);

                const perSicilBilgiKontrolRequest = { vatandaslikNo: user.tcKimlikNo };
                const perSicilBilgiKontrolResponse = await MemurIzinTalepAPI.perSicilBilgiKontrol(perSicilBilgiKontrolRequest);

                if (perSicilBilgiKontrolResponse.data.sonuc !== 0) {
                    toast.error(perSicilBilgiKontrolResponse.data.sonucAciklama);
                    return;
                }

                setPerSicilBilgiKontrol(perSicilBilgiKontrolResponse.data);

                const memurIsci = perSicilBilgiKontrolResponse.data.memurIsci;
                const sicilId = perSicilBilgiKontrolResponse.data.qtSicilId;

                // Memur veya İşçi/Taşeron/Sözleşmeli ise izin listesini yükle
                if (memurIsci === 'M' || ['I', 'T', 'K'].includes(memurIsci)) {
                    await loadIzinTalepList(memurIsci, sicilId);
                }
            } catch (err) {
                console.error('Hata:', err);
                toast.error("Sayfa yüklenirken hata oluştu.");
            } finally {
                setIsLoad(false);
            }
        };

        sayfaYukle();
    }, []);

    // Yeni talep butonuna tıklandığında
    const handleYeniTalep = () => {
        navigate('/personel-islemleri/izin-basvuru');
    };

    // Edit butonuna tıklandığında
    const handleEdit = (row: any) => {
        // Onaya gönderilmiş, onaylanmış veya reddedilmiş izinler düzenlenemez
        if (row.ctDurum === 1 || row.ctDurum === 2 || row.ctDurum === 3) {
            toast.warning("Onaya gönderilmiş, onaylanmış veya reddedilmiş izinler düzenlenemez.");
            return;
        }
        // IzinBasvuru sayfasına sqId ile yönlendir
        navigate('/personel-islemleri/izin-basvuru', { state: { sqId: row.sqId } });
    };

    // Silme butonuna tıklandığında
    const handleDeleteClick = (row: any) => {
        // Onaya gönderilmiş, onaylanmış veya reddedilmiş izinler silinemez
        if (row.ctDurum === 1 || row.ctDurum === 2 || row.ctDurum === 3) {
            toast.warning("Onaya gönderilmiş, onaylanmış veya reddedilmiş izinler silinemez.");
            return;
        }
        setSelectedDeleteItem(row);
        setDeleteDialogOpen(true);
    };

    // Silme onaylandığında
    const handleDeleteConfirm = async () => {
        if (!selectedDeleteItem || !perSicilBilgiKontrol) return;

        try {
            setIsDeleting(true);

            let response;
            const memurIsci = perSicilBilgiKontrol.memurIsci;

            if (isMemur(memurIsci)) {
                // Memur için MEM_IZIN_TALEP_DEL
                const request = new MemIzinTalepDelRequest(selectedDeleteItem.sqId);
                response = await MemurIzinTalepAPI.memIzinTalepDel(request);
            } else if (isIsci(memurIsci)) {
                // İşçi/Taşeron/Sözleşmeli için ISM_IZIN_TALEP_DEL
                const request = new IsmIzinTalepDelRequest(selectedDeleteItem.sqId);
                response = await IsciIzinTalepAPI.ismIzinTalepDel(request);
            }

            if (response?.data) {
                const result = Array.isArray(response.data) ? response.data[0] : response.data;
                const sonuc = result.sonuc;
                const sonucAciklama = result.sonucAciklama;

                if (sonuc === 0) {
                    toast.success(sonucAciklama || "Kayıt başarıyla silindi.");
                    // Listeyi yenile
                    await loadIzinTalepList(
                        perSicilBilgiKontrol.memurIsci,
                        perSicilBilgiKontrol.qtSicilId
                    );
                } else {
                    toast.error(sonucAciklama || "Silme işlemi sırasında hata oluştu.");
                }
            }
        } catch (err: any) {
            console.error('Silme hatası:', err);
            toast.error("Silme işlemi sırasında bir hata oluştu.");
        } finally {
            setIsDeleting(false);
            setDeleteDialogOpen(false);
            setSelectedDeleteItem(null);
        }
    };

    if (isLoad) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                <CircularProgress />
            </div>
        );
    }

    return (
        <>
            <div className="row">
                <div className="col-12 mb-3 d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">İzin Talep İşlem</h5>
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={<AddIcon />}
                        onClick={handleYeniTalep}
                    >
                        Yeni Talep
                    </Button>
                </div>

                <div className="col-12">
                    <div className="card">
                        <div className="card-body p-0">
                            <TableComponent
                                columns={columns}
                                data={izinTalepList}
                                recordsPerPage={10}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Silme Onay Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Silme Onayı</DialogTitle>
                <DialogContent>
                    Bu izin talebini silmek istediğinizden emin misiniz?
                    {selectedDeleteItem && (
                        <div className="mt-2">
                            <strong>İzin Türü:</strong> {selectedDeleteItem.ctTur}<br />
                            <strong>Tarih:</strong> {formatDate(selectedDeleteItem.dtBaslangicTarihi)} - {formatDate(selectedDeleteItem.dtBitisTarihi)}
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
                        İptal
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                        disabled={isDeleting}
                    >
                        {isDeleting ? <CircularProgress size={20} color="inherit" /> : "Sil"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default IzinTalepIslem;
